/**
 * ShipmentItemStatus - Situação do equipamento na pesquisa/lista de itens da remessa.
 *
 * Bolinha colorida + texto neutro (padrão do Estoque, via stock-presentation).
 * Kit: mostra também os componentes (IMEI do rastreador e ICCID do chip) com a
 * situação de cada um, vindos de get_shipment_stock_candidates v1.3.0 /
 * get_shipment_details v1.2.0.
 *
 * RELEASE: 2026-10-16 v1.0.0 - Versão inicial.
 */
import { cn } from '@/lib/utils';
import { isStockStatusOperational, stockStatusLabel, type StockPresentationKind } from '@/lib/stock-presentation';
import type { ShipmentKitComponent } from '@/hooks/useShipments';

/** Bolinha + texto da situação (verde = operacional; âmbar = demais). */
export function ShipmentStatusDot({ kind, status }: { kind: StockPresentationKind; status: string | null | undefined }) {
  if (!status) return null;
  const ok = isStockStatusOperational(kind, status);
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', ok ? 'bg-success' : 'bg-warning')} aria-hidden />
      {stockStatusLabel(kind, status)}
    </span>
  );
}

interface Props {
  type: string;
  status?: string | null;
  components?: ShipmentKitComponent[] | null;
}

/** Linha de situação do item; para kit, inclui os componentes. */
export function ShipmentItemStatusLine({ type, status, components }: Props) {
  if (type !== 'tracker' && type !== 'chip' && type !== 'kit') return null;
  return (
    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
      <ShipmentStatusDot kind={type} status={status} />
      {type === 'kit' && (components ?? []).map((c) => (
        <span key={`${c.type}:${c.label}`} className="inline-flex min-w-0 items-center gap-1.5 text-xs">
          <span className="text-muted-foreground">{c.type === 'tracker' ? 'IMEI' : 'ICCID'}</span>
          <span className="truncate font-mono">{c.label ?? '—'}</span>
          <ShipmentStatusDot kind={c.type} status={c.status} />
        </span>
      ))}
    </div>
  );
}
